import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Image as ImageIcon, RefreshCw, Loader2 } from "lucide-react";
import { api } from "../../lib/api";

export default function AdminPhotoCoverage({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/photos/coverage", {
        headers: { "X-Admin-Token": token },
      });
      setData(r.data);
    } catch {
      // silently ignore — admin will see no card render
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const runRegistryRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await api.post(
        "/admin/photos/registry-refresh",
        {},
        { headers: { "X-Admin-Token": token } }
      );
      const attached = r.data?.attached ?? 0;
      if (attached > 0) {
        toast.success(`Registry refresh: attached ${attached} new photo${attached === 1 ? "" : "s"}.`);
      } else {
        toast.info("Registry refresh complete — no new matches.");
      }
      await load();
    } catch {
      toast.error("Refresh failed — check backend logs.");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading || !data) return null;
  const { adjudicated_total, with_photo, coverage_pct } = data;
  const remaining = adjudicated_total - with_photo;

  return (
    <div
      className="border border-zinc-900 bg-white p-5"
      data-testid="admin-photo-coverage"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <ImageIcon size={16} className="text-red-600" />
          <div className="label-eyebrow">Photo Coverage</div>
        </div>
        <button
          type="button"
          onClick={runRegistryRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest border border-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors disabled:opacity-60"
          title="Re-match unphotographed convicted records against the state registry"
          data-testid="admin-photo-refresh"
        >
          {refreshing ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          {refreshing ? "Refreshing" : "Refresh"}
        </button>
      </div>

      <div className="mt-3 flex items-baseline gap-2">
        <div
          className="font-display font-black text-4xl tracking-tighter"
          data-testid="admin-photo-coverage-pct"
        >
          {coverage_pct}%
        </div>
        <div className="text-sm text-zinc-600">
          <span className="font-bold text-zinc-900" data-testid="admin-photo-coverage-with">
            {with_photo}
          </span>{" "}
          of{" "}
          <span className="font-bold text-zinc-900" data-testid="admin-photo-coverage-total">
            {adjudicated_total}
          </span>{" "}
          convicted records
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-3 h-1.5 bg-zinc-200 overflow-hidden">
        <div
          className="h-full bg-red-600 transition-all duration-500"
          style={{ width: `${Math.min(coverage_pct, 100)}%` }}
          aria-hidden="true"
        />
      </div>

      <p className="mt-3 text-xs text-zinc-600 leading-relaxed">
        {remaining > 0 ? (
          <>
            <strong>{remaining}</strong> convicted record{remaining === 1 ? "" : "s"} still
            without a photo. Open any case below and use the photo editor to add one — or
            click <strong>Refresh</strong> to re-run the state registry match.
          </>
        ) : (
          <>Every adjudicated record has a photo attached. Great work.</>
        )}
      </p>
    </div>
  );
}
